import moment from "moment";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import LoadingScreen from "../components/common/LoadingScreen";
import { fetchAuth } from "../reducers/dashboard/DashboardActions";
import AuthHelper from "../utils/AuthHelper";
import commerceAPI from "../utils/CommerceAPI";
import Constants from "../utils/Constants";
import { sendLogin as sendLoginTag } from "../utils/TagManagerHelper";
import RouteConstants from "../utils/RouteConstants";
import AWSAPI from "../utils/AWSAPI";
import {
  msalInstance,
  b2cPolicies,
  apiScope,
  ensureMsalInitialized,
  logout,
} from "../config/AuthProvider";

export const MsalContext = React.createContext();
export const useMsalContext = () => useContext(MsalContext);

function AzureWrapperContent({ children }) {
  const { REACT_APP_MAINTENANCE_MODE } = process.env;
  const urlParams = new URLSearchParams(window.location.search);
  const isMaintenance =
    REACT_APP_MAINTENANCE_MODE === "true" &&
    !urlParams.has(process.env.REACT_APP_MAINTENANCE_MODE_BACKDOOR);

  const [isLoading, setIsLoading] = useState(true);
  const [isMsalReady, setIsMsalReady] = useState(false);
  const [isRedirectHandling, setIsRedirectHandling] = useState(false);
  const [isLoginRedirectInProgress, setIsLoginRedirectInProgress] = useState(false);
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const REFRESH_THRESHOLD_MINUTES = 0;
  const dispatch = useDispatch();

  // Wait for MSAL to be ready - use centralized initialization
  useEffect(() => {
    const checkMsalReady = async () => {
      try {
        await ensureMsalInitialized();
        setIsMsalReady(true);
      } catch (error) {
        // Continue anyway to prevent blocking the app
        setIsMsalReady(true);
      }
    };

    checkMsalReady();
  }, []);

  const isPathnamePublic = (pathname) => {
    const enabledEndpoints = [
      "/forgot-password",
      "/change-password",
      "/signup-confirmation",
      "/country-not-available",
      "/invitation-error",
      "/service-maintenance",
      "/membership",
      "/payment-successful",
      "/logout",
      RouteConstants.CHAT_PAGE_URL,
      `(${RouteConstants.PRODUCT_PAGE_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.INSIGHTS_PAGE_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.HIRE_A_TEAM_PAGE_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.VIDEO_LIBRARY_URL})([A-Za-z0-9])*`,
      `(${RouteConstants.AREA42LABS_PAGE_URL})([A-Za-z0-9])*`,
      "/accessibility-statement",
    ];

    return enabledEndpoints.some((enabledPath) => {
      const regex = new RegExp(enabledPath);
      return regex.test(pathname) || pathname === "/";
    });
  };

  const setLoadingStateFinished = (forceRedirect = false) => {
    if (forceRedirect) {
      AuthHelper.redirectToSavedLocation(setIsLoading);
    } else if (!isLoginRedirectInProgress) {
      // Only stop loading if we're not in the middle of a login redirect
      setIsLoading(false);
    }
  };

  const executeNewLogin = async () => {
    // eslint-disable-next-line no-console
    console.log("executeNewLogin: Starting login process");

    let response;
    let authorityToUse = b2cPolicies.authorities.signIn.authority; // Declare outside try-catch

    try {
      // Get the B2C authority from the account
      const account = accounts[0];
      // const accountAuthority = account?.idTokenClaims?.iss || account?.environment;
      const policyUsed = account?.idTokenClaims?.tfp;

      // Determine which B2C policy authority to use
      if (policyUsed) {
        // Map the policy to the correct authority
        if (policyUsed.includes("signup") || policyUsed.includes("SignUp")) {
          authorityToUse = b2cPolicies.authorities.signUp.authority;
        } else if (policyUsed.includes("passwordreset") || policyUsed.includes("PasswordReset")) {
          authorityToUse = b2cPolicies.authorities.resetPassword.authority;
        } else if (policyUsed.includes("forgotpassword") || policyUsed.includes("ForgotPassword")) {
          authorityToUse = b2cPolicies.authorities.forgotPassword.authority;
        }
      }

      response = await instance.acquireTokenSilent({
        ...apiScope,
        account: accounts[0],
        authority: authorityToUse,
      });
    } catch (error) {
      // Fallback with acquireTokenRedirect for interaction required errors
      if (error instanceof InteractionRequiredAuthError || error.errorCode === "AADB2C90027") {
        await instance.acquireTokenRedirect({
          ...apiScope,
          account: accounts[0],
          authority: authorityToUse, // Now accessible in catch block
        });
        return;
      }

      commerceAPI.defaultErrorHandler(error);
      return;
    }

    // Always execute commerceAPI logic if we have a response
    // Check if login has already been completed using the same mechanism as sendLoginTag
    if (!sessionStorage.getItem("loggedin")) {
      // eslint-disable-next-line no-console
      console.log("executeNewLogin: No login session found, proceeding with login");

      if (!sessionStorage.getItem(Constants.IS_LOGGED_IN_USER_DASHBOARD_ENABLED)) {
        dispatch(fetchAuth());
      }

      commerceAPI
        .login(response.accessToken)
        .then((apiResponse) => {
          const status = apiResponse?.status;
          const approved = apiResponse?.data?.approved;

          // eslint-disable-next-line no-console
          console.log("executeNewLogin: Commerce API login response", {
            status,
            approved,
            isReplyer: apiResponse.data.isReplyer,
          });

          // 200 + approved true: go home (normal flow)
          if (status === 200 && approved === true) {
            // eslint-disable-next-line no-console
            console.log(
              "executeNewLogin: Status 200 with approved true - proceeding with normal flow",
            );

            sessionStorage.setItem(Constants.IS_LOGGED_IN_USER_REPLYER, apiResponse.data.isReplyer);

            // Send login tag after successful commerce API login
            sendLoginTag();

            const reqBody = { email: AuthHelper.getEmail() };
            // eslint-disable-next-line no-console
            console.log("executeNewLogin: Checking qualified credentials for email", reqBody.email);

            AWSAPI.isQualifiedCredentials(reqBody).then((resAPI) => {
              // eslint-disable-next-line no-console
              console.log("executeNewLogin: Qualified credentials check completed", {
                isQualifiedIT: resAPI.data.isQualifiedIT,
              });
              sessionStorage.setItem(
                Constants.IS_LOGGED_IN_USER_QUALIFIED_INNOVATION_TOUR,
                resAPI.data.isQualifiedIT,
              );
            });

            // eslint-disable-next-line no-console
            console.log("executeNewLogin: Setting loading state finished with redirect");
            setLoadingStateFinished(true);
          }
          // 200 + approved false: go to signup-confirmation page
          else if (status === 200 && approved === false) {
            // eslint-disable-next-line no-console
            console.log(
              "executeNewLogin: Status 200 with approved false - redirecting to signup-confirmation",
            );
            setLoadingStateFinished(false);
            window.location.replace("/signup-confirmation");
          }
          // Unexpected response
          else {
            // eslint-disable-next-line no-console
            console.log("executeNewLogin: Unexpected response - logging out", {
              status,
              approved,
            });
            setLoadingStateFinished(false);
            logout();
          }
        })
        .catch((e) => {
          const status = e?.response?.status;

          // eslint-disable-next-line no-console
          console.error("executeNewLogin: Commerce API login failed", {
            error: e.message,
            status,
          });

          // 401: logout
          if (status === 401) {
            // eslint-disable-next-line no-console
            console.log("executeNewLogin: Status 401 received - logging out");
            setLoadingStateFinished(false);
            logout();
          }
          // 412: handle specially (existing behavior)
          else if (status === 412) {
            // eslint-disable-next-line no-console
            console.log(
              "executeNewLogin: Status 412 received, setting loading finished without redirect",
            );
            setLoadingStateFinished(false);
          }
          // Other errors: use default error handler
          else {
            commerceAPI.defaultErrorHandler(e);
            setLoadingStateFinished(false);
          }
        });
    } else {
      // eslint-disable-next-line no-console
      console.log(
        "executeNewLogin: Login session already exists, setting loading finished with redirect",
      );
      setLoadingStateFinished(true);
    }

    // Add auth cookie for dam/authorizer
    const { accessToken } = response;
    fetch(`${process.env.REACT_APP_AWS_API}/auth/set-cookie`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: accessToken, expiresOn: response.expiresOn }),
      // eslint-disable-next-line no-console
    }).catch((err) => console.error("rose-authorizer set-cookie failed:", err));
  };

  const isPathnameBreakingEarly = (pathname) => {
    const pathnames = [
      "/signup-confirmation",
      "/country-not-available",
      "/invitation-error",
      "/service-maintenance",
      "/forgot-password",
      "/change-password",
      "/membership",
      "/payment-successful",
      RouteConstants.CHAT_PAGE_URL,
    ];

    return pathnames.includes(pathname);
  };

  const requestNewToken = async () => {
    if (accounts[0]?.idTokenClaims?.exp) {
      // Re-enable token refresh check
      if (
        moment
          .unix(accounts[0].idTokenClaims.exp)
          .isBefore(moment().add(REFRESH_THRESHOLD_MINUTES, "minutes"))
      ) {
        try {
          // eslint-disable-next-line no-console
          console.log("requestNewToken: Token expiring soon, attempting silent refresh");

          await instance.acquireTokenSilent({
            ...apiScope,
            account: accounts[0],
          });

          // eslint-disable-next-line no-console
          console.log("requestNewToken: Silent token refresh successful");
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("requestNewToken: Silent token refresh failed", {
            errorType: error.constructor.name,
            errorCode: error.errorCode,
            errorMessage: error.message,
          });

          // If silent token acquisition fails, sign out
          logout();
          return;
        }
      }

      // Execute login process
      await executeNewLogin();
    }
  };

  useEffect(() => {
    if (!isMsalReady) return; // Wait for MSAL to be ready
    if (isRedirectHandling) return; // Don't interfere with redirect handling

    const k = async () => {
      if (!isMaintenance) {
        if (urlParams.has("redirect") && urlParams.get("redirect") === "home") {
          window.history.pushState({}, document.title, "/");
          AuthHelper.clearSavedRedirectLocation();
        }

        // special case for pathnames that respond to loggin in errors
        // and the change password flow
        const { pathname } = window.location;
        if (isPathnamePublic(pathname) && isPathnameBreakingEarly(pathname)) {
          setLoadingStateFinished(false);
          if (pathname === "/change-password") {
            // setBlockNotifications(false);
          }
          return undefined; // Explicitly return undefined
        }

        if (isAuthenticated && accounts.length > 0) {
          const account = accounts[0];
          if (!AuthHelper.isRegisteredFromReply(account.idTokenClaims?.email || "")) {
            AuthHelper.isCountryAllowed(account.idTokenClaims?.countryCode);
          }

          if (
            account.idTokenClaims?.tfp === Constants.TFP_FORGOT_PASSWORD_FLOW ||
            account.idTokenClaims?.tfp === Constants.TFP_RESET_PASSWORD_FLOW
          ) {
            setLoadingStateFinished(true);
            return undefined; // Explicitly return undefined
          }

          await requestNewToken();
        } else if (isPathnamePublic(window.location.pathname)) {
          setLoadingStateFinished(false);
        } else {
          try {
            setIsRedirectHandling(true);
            setIsLoginRedirectInProgress(true);

            // Keep loading state active during redirect
            instance.loginRedirect({
              scopes: ["openid", "profile"],
              authority: b2cPolicies.authorities.signIn.authority,
            });

            // The page will redirect, so we don't need to handle the loading state here
            // The loading will continue until the redirect completes and the user comes back
          } catch (error) {
            setIsRedirectHandling(false);
            setIsLoginRedirectInProgress(false);
            setLoadingStateFinished(false);
          }
        }
      } else {
        setLoadingStateFinished();
      }
      return undefined; // Explicitly return undefined at the end
    };

    k().catch((e) => {
      // eslint-disable-next-line no-console
      console.error("Error in AzureWrapper useEffect:", e);
      setIsRedirectHandling(false);
      setIsLoginRedirectInProgress(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accounts, isMaintenance, isMsalReady, isRedirectHandling]);

  // Re-enable token expiration monitoring
  useEffect(() => {
    if (!isMsalReady) return undefined; // Wait for MSAL to be ready

    const checkTokenExpiration = async () => {
      try {
        // Check if token exists and has expiration
        if (accounts[0]?.idTokenClaims?.exp) {
          const expirationMoment = moment.unix(accounts[0].idTokenClaims.exp);
          const currentMoment = moment();
          const minutesUntilExpiration = expirationMoment.diff(currentMoment, "minutes");

          // eslint-disable-next-line no-console
          console.log("Token expiration check:", {
            minutesUntilExpiration,
            threshold: REFRESH_THRESHOLD_MINUTES,
            willRefresh: minutesUntilExpiration <= REFRESH_THRESHOLD_MINUTES,
          });

          if (minutesUntilExpiration <= REFRESH_THRESHOLD_MINUTES) {
            // eslint-disable-next-line no-console
            console.log("Token refresh triggered by expiration check");
            await requestNewToken();
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Token expiration check failed:", error);
        await requestNewToken();
      }
    };

    // Initial check
    checkTokenExpiration();

    // Set up interval to check every 3 minutes
    const intervalId = setInterval(checkTokenExpiration, 3 * 60 * 1000);

    // eslint-disable-next-line no-console
    console.log("Token expiration monitor started, checking every 3 minutes");

    // Clean up interval on component unmount
    return () => {
      clearInterval(intervalId);
      // eslint-disable-next-line no-console
      console.log("Token expiration monitor stopped");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, isMsalReady]);

  // Determine if we should show loading
  const shouldShowLoading = isLoading || isLoginRedirectInProgress;

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <MsalContext.Provider value={{ isLoading: shouldShowLoading }}>
      <RenderChildren isLoading={shouldShowLoading}>{children}</RenderChildren>
    </MsalContext.Provider>
  );
}

AzureWrapperContent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

AzureWrapperContent.defaultProps = {
  children: () => null,
};

export function AzureWrapper({ children }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AzureWrapperContent>{children}</AzureWrapperContent>
    </MsalProvider>
  );
}

AzureWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

AzureWrapper.defaultProps = {
  children: () => null,
};

function RenderChildren({ children, isLoading }) {
  if (isLoading) {
    return <LoadingScreen />;
  }
  return <> {children} </>;
}

RenderChildren.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
};
